import { cloneDeep, pull, pullAll } from 'lodash';
import { Framework, type IHistoryItem } from '@/framework';
import { RealEditMerge } from '@/layers/layer/real-edit-merge';
import { traverseTree } from '@/utils/tree-kit';
import type { IGroupLayerProps, ILayerProps } from '@/version-types';
import { HistoryType, type IHistoryMergeData, type IHistoryPackData, type IHistoryUnpackData } from './type';

export class GroupHistory extends Framework {
    private realEditMerge: RealEditMerge;

    constructor(identity: string) {
        super(identity);
        this.realEditMerge = new RealEditMerge(this.framework);
        this.registerPackHistory();
        this.registerUnpackHistory();
        this.registerMergeHistory();
    }

    /** 合并 */
    private registerMergeHistory() {
        this.framework.service.history.register(HistoryType.MERGE, {
            owner: 'layer/group',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryMergeData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { delData, insertData } = historyItem.data.data;
                const { oldLayer, oldLayerIndex, childIds, childIndexes } = delData.item;
                const isExtraGroup = this.framework.service.flatLayer.checkIsExtraGroup(oldLayer);
                const originIndex = historyItem.originIndex;
                const originIds = this.framework.service.frame.getLayerIds(originIndex);
                if (isRedo) {
                    const delIds = [...childIds, oldLayer.id];
                    pullAll(originIds, delIds);
                    delIds.forEach((id) => {
                        this.framework.service.flatLayer.delete(id);
                    });
                    this.framework.service.frame.addOrUpdateLayer(insertData, originIndex);
                    this.framework.service.frame.setActiveLayer(insertData.id);
                }
                if (isUndo) {
                    this.framework.service.frame.delLayer(insertData.id);
                    pullAll(originIds, [...childIds, oldLayer.id]);
                    if (isExtraGroup) {
                        for (let i = 0; i < childIds.length; i++) {
                            const id = childIds[i];
                            const index = childIndexes[i];
                            originIds.splice(index, 0, id);
                        }
                    }
                    originIds.splice(oldLayerIndex, 0, oldLayer.id);
                    this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                    this.framework.service.frame.setActiveLayer(oldLayer.id);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryMergeData>) => {
                const originIds = this.framework.service.frame.getRenderIdsWithoutExtraGroup(historyItem.originIndex);
                return {
                    message: [originIds, historyItem.originIndex, historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryMergeData>) => {
                const originIds = this.framework.service.frame.getRenderIdsWithoutExtraGroup(historyItem.originIndex);
                return {
                    message: [originIds, historyItem.originIndex, historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IHistoryMergeData>, isRedo, isUndo, store) => {
                if (isUndo) {
                    historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                        originLayer: historyItem.data.data.insertData,
                    });
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const originLayer = historyItem.data.data.delData.item.oldLayer;
                        const latestIsExtraGroup = this.framework.service.flatLayer.checkIsExtraGroup(originLayer);
                        const latestIsFormalGroup = this.framework.service.flatLayer.checkIsFormalGroup(originLayer);
                        historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                            originLayer,
                            latestIsExtraGroup,
                            latestIsFormalGroup,
                        });
                    }
                }
            },
            onMessage: (message: [string[], number, IHistoryMergeData], isRedo, isUndo, isWrite) => {
                const [originIds, originIndex, mergeData] = message;
                const { delData, insertData } = mergeData;
                const { oldLayer, childIds } = delData.item;
                const currentOriginIds = this.framework.service.frame.getLayerIds(originIndex);
                currentOriginIds.length = 0;
                currentOriginIds.push(...originIds);

                if (isRedo || isWrite) {
                    this.checkIsDirtyActiveInfo(oldLayer);
                    const delIds = [...childIds, oldLayer.id];
                    delIds.forEach((id) => {
                        this.framework.service.flatLayer.delete(id);
                    });
                    this.framework.service.frame.addOrUpdateLayer(insertData, originIndex);
                }
                if (isUndo) {
                    this.checkIsDirtyActiveInfo(insertData);
                    // pull(currentOriginIds, insertData.id);
                    this.framework.service.flatLayer.delete(insertData.id);

                    const isExtraGroup = this.framework.service.flatLayer.checkIsExtraGroup(oldLayer);
                    if (isExtraGroup) {
                        // pull(currentOriginIds, oldLayer.id);
                        this.framework.service.flatLayer.delete(oldLayer.id);
                        for (let i = 0; i < oldLayer.children?.length; i++) {
                            const layer = oldLayer.children[i];
                            layer.parentId = undefined;
                            this.framework.service.frame.addOrUpdateLayer(layer, originIndex);
                        }
                    } else {
                        this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                    }
                }

                // this.framework.service.frame.rerender(originIndex);
            },
        });
    }

    /** 成组 */
    private registerPackHistory() {
        this.framework.service.history.register(HistoryType.PACK, {
            owner: 'layer/group',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryPackData>, isRedo: boolean, isUndo: boolean) => {
                const { item } = historyItem.data.data;
                const { childIndexes, childIds, layerIndex } = item;
                const dirtyLayer = (isRedo ? item.oldLayer : item.newLayer) || item.layer;
                historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                    originLayer: dirtyLayer,
                    latestIsExtraGroup: isRedo,
                    latestIsFormalGroup: isUndo,
                });
                if (historyItem.isDirty) return;
                this.framework.service.frame.delActiveLayer();
                const originIndex = historyItem.originIndex;
                const originIds = this.framework.service.frame.getLayerIds(historyItem.originIndex);
                const snapshotLayer = isUndo ? item.oldLayer : item.newLayer;
                const nextLayer = cloneDeep(snapshotLayer || item.layer);
                if (isRedo) {
                    pullAll(originIds, [...childIds, nextLayer.id]);
                    originIds.splice(layerIndex, 0, nextLayer.id);
                }
                if (isUndo) {
                    pull(originIds, nextLayer.id);
                    for (let i = 0; i < childIndexes.length; i++) {
                        const index = childIndexes[i];
                        const id = childIds[i];
                        originIds.splice(index, 0, id);
                    }
                    originIds.push(nextLayer.id);
                }
                if (snapshotLayer) {
                    this.resetSnapshotGroupChild(nextLayer);
                } else {
                    this.resetGroupChild(nextLayer);
                }
                (nextLayer.data as IGroupLayerProps).isExtraGroup = isUndo;
                item.layer = cloneDeep(nextLayer);
                this.framework.service.frame.addOrUpdateLayer(nextLayer, originIndex);
                this.framework.service.frame.emitSortLayer(originIndex);
                this.framework.service.frame.setActiveLayer(nextLayer.id);
            },
            onMerge: this.realEditMerge.merge.bind(this),
            onAfterWrite: this.getGroupMessage.bind(this),
            onAfterRedoUndo: this.getGroupMessage.bind(this),
            onMessage: this.processGroupMessage.bind(this),
        });
    }

    /** 解组 */
    private registerUnpackHistory() {
        this.framework.service.history.register(HistoryType.UNPACK, {
            owner: 'layer/group',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryUnpackData>, isRedo: boolean, isUndo: boolean) => {
                const { item } = historyItem.data.data;
                const { layer, layerIndex } = item;
                historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                    originLayer: layer,
                    latestIsExtraGroup: isUndo,
                    latestIsFormalGroup: isRedo,
                });
                // console.log('ot unpack onRedoUndo', { isDirty: historyItem.isDirty, isRedo, isUndo });
                if (historyItem.isDirty) return;
                this.framework.service.frame.delActiveLayer();
                const originIndex = historyItem.originIndex;
                const originIds = this.framework.service.frame.getLayerIds(originIndex);
                const childIds: string[] = (layer.children || []).map((v) => v.id);
                if (isRedo) {
                    pull(originIds, layer.id);
                    originIds.splice(layerIndex, 0, ...childIds);
                    originIds.push(layer.id);
                    layer.lock = false;
                }
                if (isUndo) {
                    pullAll(originIds, [...childIds, layer.id]);
                    originIds.splice(layerIndex, 0, layer.id);
                }
                this.resetGroupChild(layer);
                (layer.data as IGroupLayerProps).isExtraGroup = isRedo;
                this.framework.service.frame.addOrUpdateLayer(layer, originIndex);
                this.framework.service.frame.emitSortLayer(originIndex);
                this.framework.service.frame.setActiveLayer(layer.id);
            },
            onMerge: this.realEditMerge.merge.bind(this),
            onAfterWrite: this.getGroupMessage.bind(this),
            onAfterRedoUndo: this.getGroupMessage.bind(this),
            onMessage: this.processGroupMessage.bind(this),
        });
    }

    /** 重置组的子图层 */
    private resetGroupChild(layer: ILayerProps) {
        const newChildren: ILayerProps[] = [];
        layer.children?.forEach((item) => {
            const childLayer = this.framework.service.flatLayer.getLayer(item.id, true);
            if (childLayer) {
                childLayer.parentId = layer.id;
                newChildren.push(childLayer);
            }
        });
        layer.children = newChildren;
    }

    /** 重置历史快照子图层的父级，不从当前 flatLayer 取最新位置。 */
    private resetSnapshotGroupChild(layer: ILayerProps) {
        layer.children?.forEach((item) => {
            item.parentId = layer.id;
        });
    }

    /** 处理同步的数据 */
    private getGroupMessage(historyItem: IHistoryItem<IHistoryPackData | IHistoryUnpackData>) {
        return {
            message: [historyItem.originIndex, historyItem.data.data.item.layer],
        };
    }

    private checkIsDirtyActiveInfo(layer: ILayerProps) {
        const activeInfo = this.framework.service.frame.getActiveInfo();
        let isDirtyActiveInfo = false;
        if (activeInfo) {
            // 激活图层是临时组，并且包含了组的图层，则删除激活图层
            const activeLayer = this.framework.service.flatLayer.getLayer(activeInfo.info.id);
            if (activeLayer) {
                if (this.framework.service.flatLayer.checkIsExtraGroup(activeLayer)) {
                    const activeIds: Set<string> = new Set();
                    traverseTree(activeLayer, (node) => {
                        activeIds.add(node.id);
                    });
                    traverseTree(layer, (node) => {
                        if (activeIds.has(node.id)) {
                            isDirtyActiveInfo = true;
                        }
                    });
                } else {
                    traverseTree(layer, (node) => {
                        if (node.id === activeInfo.info.id || node.id === activeInfo.info.pickId) {
                            isDirtyActiveInfo = true;
                        }
                    });
                }
            }
        }

        if (isDirtyActiveInfo) {
            this.framework.service.frame.delActiveLayer();
        }

        return {
            isDirtyActiveInfo,
            activeInfo,
        };
    }

    /** 处理组的同步消息 */
    private processGroupMessage(message: [number, ILayerProps]) {
        const [originIndex, layer] = message;
        const { activeInfo } = this.checkIsDirtyActiveInfo(layer);
        const currentOriginIds = this.framework.service.frame.getLayerIds(originIndex);

        if (this.framework.service.flatLayer.checkIsExtraGroup(layer)) {
            let layerIndex = currentOriginIds.indexOf(layer.id);
            if (layerIndex === -1) {
                layerIndex = currentOriginIds.length - 1;
            }
            this.framework.service.flatLayer.delete(layer.id);
            const childLayers: ILayerProps[] = [];
            const childIds: string[] = [];
            console.log('ot unpack', cloneDeep(layer));

            layer.children?.forEach((item) => {
                item.parentId = undefined;
                this.framework.service.frame.addOrUpdateLayer(item, originIndex);
                childLayers.push(item);
                childIds.push(item.id);
            });
            pullAll(currentOriginIds, [...childIds, layer.id]);
            currentOriginIds.splice(layerIndex, 0, ...childIds);

            this.framework.service.frame.emitSortLayer(originIndex);
        } else {
            const childIds: string[] = [];
            traverseTree(layer, (node) => {
                childIds.push(node.id);
            });
            pullAll(currentOriginIds, childIds);
            this.framework.service.frame.addOrUpdateLayer(layer, originIndex);
            this.framework.service.frame.emitSortLayer(originIndex);
        }
        if (activeInfo) {
            const activeId = activeInfo.info.pickId || activeInfo.info.id;
            this.framework.service.frame.setActiveLayer(activeId);
        }
    }
}
